/**
 * React Query hooks for Clubs/Organizations
 *
 * Replaces the old useClubs hook with React Query for better caching and state management.
 *
 * Usage:
 * ```typescript
 * import { useClubsQuery } from '@/lib/platform/hooks/queries/use-clubs-query';
 *
 * function MyComponent() {
 *   const { data: clubs, isLoading, error } = useClubsQuery();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *
 *   return <div>{clubs.length} clubs found</div>;
 * }
 * ```
 */

'use client';

import { fetchWithBypass } from '../../../platform/utils/fetch';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from './query-keys';
import { Club } from '@/lib/platform/types';
import { useSession } from '@/lib/platform/auth/client';

/**
 * Fetch all organizations for the current user
 */
async function fetchClubs(): Promise<Club[]> {
  const response = await fetchWithBypass('/api/organizations');

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch organizations');
  }

  const data = await response.json();
  return data.organizations || [];
}

/**
 * Hook to fetch and cache all clubs for the current user
 *
 * Features:
 * - Automatic caching (5 minutes stale time)
 * - Background refetching
 * - Automatic retries on failure
 * - Request deduplication
 */
export function useClubsQuery() {
  const { data: session } = useSession();

  return useQuery({
    queryKey: session?.user?.id
      ? [...queryKeys.clubs.lists(), session.user.id]
      : queryKeys.clubs.lists(),
    queryFn: fetchClubs,
    enabled: !!session?.user, // Only fetch if user is logged in
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}
